import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { ApplicationView, useController } from '@aha-app/mvc';
import markdownit from 'markdown-it';
import markdownLinkAttributes from 'markdown-it-link-attributes';
import styles from './ConversationPanel.module.css';
import { type MessageProps } from './MessageContent';
import type { AssistantViewController } from '../AssistantView/AssistantViewController';
import BlankLinkTo from 'components/common/BlankLinkTo';
import SanitizeInnerHTML from 'components/SanitizeInnerHTML/SanitizeInnerHTML';
import { sanitizeUrl } from 'javascripts/sanitizer';

// Use negative lookahead/lookbehind assertions to avoid matching inside <a> tags
const REFERENCE_REGEX =
  /(?<![<\w]href=["'][^>]*?)(?!<a\b[^>]*>[^<]*?)(?<!<a\b[^>]*>[^<]*?)[A-Z][A-Z0-9]*(?=.*-\d+)(-(?:[A-Z]+|\d+))+(?![^<]*?<\/a>)/g;
// Match citations like <aha-cite title="Title of page" url="https://example.com" />
const CITATION_REGEX = /<aha-cite\s+title="([^"]+)"\s+url="([^"]+)"\s*\/>/g;

export const renderCitesAsMarkdownLinks = (body: string) => {
  return body.replace(CITATION_REGEX, (match, title, url) => {
    return '[' + title + '](' + sanitizeUrl(url) + ')';
  });
};

const MarkdownContent = ({ body }: MessageProps) => {
  const controller = useController<AssistantViewController>();
  let projectPrefixes: Record<string, boolean> = {};
  if (controller) {
    projectPrefixes = controller.state.projectPrefixes;
  }

  if (body.length === 0) return null;

  // Remove the "Next Steps" section from the body.
  const nextActionsRegex =
    /(?:\s*-{1,}\s*)?\s*#{1,5}\s*Next\s+(?:Actions|Steps)[\s\S]*?(?=(?:#{1,5}\s*[^\n]+)|$)/i;
  body = body.replace(nextActionsRegex, '');

  // Extract citations.
  const citations: Record<string, string>[] = [];
  body = body.replace(CITATION_REGEX, (match, title, url) => {
    citations.push({ title, url });
    return `AHA_CITATION_${citations.length - 1}`;
  });

  // Convert the markdown to HTML. Markdown-it takes responsibility for sanitizing
  // the output. https://github.com/markdown-it/markdown-it/blob/master/docs/security.md
  const md = markdownit({ breaks: true });
  // Force all links to open in a new tab.
  md.use(markdownLinkAttributes, {
    attrs: { target: '_blank', rel: 'noopener noreferrer' },
  });
  // Disable image rendering to prevent loading external images which may be an exfiltration vector.
  md.disable('image');

  // TODO: Add syntax highlighting here. Consider using https://shiki.style/packages/markdown-it or markdown-it-prism

  let htmlContent = md.render(body);

  // Replace record references with links.
  htmlContent = htmlContent.replace(REFERENCE_REGEX, match => {
    const url = `/records/${match}`;

    const projectPrefix = match.split('-')[0];
    if (projectPrefix && projectPrefixes[projectPrefix]) {
      return `<a href="${url}" data-drawer-url="${url}" class='assistant-reference'><aha-record-reference size="small">${match}</aha-record-reference></a>`;
    } else {
      if (controller) {
        controller.actionCheckProjectPrefix(projectPrefix);
      }
      return match;
    }
  });

  // Replace citations with links.
  htmlContent = htmlContent.replace(/AHA_CITATION_(\d+)/g, (match, index) => {
    let url = citations[index].url;
    // Handle if the links are in markdown format [title](url)
    if (url.startsWith('[') && url.endsWith(')')) {
      url = url.match(/\(([^)]+)\)/)?.[1] || url;
    }

    return (
      ' ' +
      ReactDOMServer.renderToString(
        <BlankLinkTo href={sanitizeUrl(url)} className={styles.citationLink}>
          {citations[index].title}
        </BlankLinkTo>
      ) +
      ' '
    );
  });

  return (
    <SanitizeInnerHTML
      element={<span className={styles.markdown} />}
      html={htmlContent}
      addAttributes={['target']}
    />
  );
};

export default ApplicationView(MarkdownContent);
